import { useForm } from '@inertiajs/react'
import AuthLayout from '@/layouts/AuthLayout'
import type { FormEvent } from 'react'

interface InvitationProps {
  invitation: {
    email_address: string
    roles: Array<'admin' | 'member'>
    account_name: string
    inviter_email_address: string | null
    expires_at: string
  }
  existing_user: boolean
  expired?: boolean
  errors?: Record<string, string>
}

export default function AcceptInvitation({ invitation, existing_user, expired, errors = {} }: InvitationProps) {
  const { data, setData, post, processing } = useForm({
    password: '',
    password_confirmation: '',
  })

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    post(`/invitations/${getTokenFromUrl()}/accept`)
  }

  function getTokenFromUrl(): string {
    const match = window.location.pathname.match(/\/invitations\/([^/]+)/)
    return match ? match[1] : ''
  }

  if (expired) {
    return (
      <AuthLayout title="Invitation expired">
        <div className="card bg-base-100 shadow-lg">
          <div className="card-body text-center">
            <span className="iconify lucide--clock size-12 mx-auto text-warning" />
            <h2 className="card-title text-xl font-bold justify-center mt-2">This invitation expired</h2>
            <p className="text-sm text-base-content/70 mt-2">
              Ask {invitation.inviter_email_address ?? 'an administrator'} to send you a new invitation to{' '}
              <strong>{invitation.account_name}</strong>.
            </p>
            <div className="mt-4">
              <a href="/login" className="link link-primary text-sm">Back to login</a>
            </div>
          </div>
        </div>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout title={`Join ${invitation.account_name}`}>
      <div className="card bg-base-100 shadow-lg">
        <div className="card-body">
          <h2 className="card-title text-2xl font-bold">Join {invitation.account_name}</h2>
          <p className="text-sm text-base-content/70 mt-1">
            {invitation.inviter_email_address ? (
              <><strong>{invitation.inviter_email_address}</strong> invited you as</>
            ) : (
              <>You&apos;ve been invited as</>
            )}{' '}
            {invitation.roles.map((role) => (
              <span key={role} className="badge badge-soft badge-primary badge-sm capitalize ms-1">
                {role}
              </span>
            ))}
          </p>

          <form onSubmit={handleSubmit} className="space-y-4 mt-6">
            {errors.password && (
              <div role="alert" className="alert alert-soft alert-error text-sm">
                {errors.password}
              </div>
            )}

            <fieldset className="fieldset">
              <legend className="fieldset-legend">Email</legend>
              <input
                type="email"
                className="input w-full"
                value={invitation.email_address}
                disabled
              />
            </fieldset>

            <fieldset className="fieldset">
              <legend className="fieldset-legend">
                {existing_user ? 'Your password' : 'Choose a password'}
              </legend>
              <input
                type="password"
                className="input w-full"
                value={data.password}
                onChange={(e) => setData('password', e.target.value)}
                required
                autoFocus
                autoComplete={existing_user ? 'current-password' : 'new-password'}
                minLength={existing_user ? undefined : 8}
              />
            </fieldset>

            {!existing_user && (
              <fieldset className="fieldset">
                <legend className="fieldset-legend">Confirm password</legend>
                <input
                  type="password"
                  className="input w-full"
                  value={data.password_confirmation}
                  onChange={(e) => setData('password_confirmation', e.target.value)}
                  required
                  autoComplete="new-password"
                  minLength={8}
                />
              </fieldset>
            )}

            <button
              type="submit"
              className="btn btn-primary w-full"
              disabled={processing}
            >
              {processing && <span className="loading loading-spinner loading-sm" />}
              Accept invitation
            </button>
          </form>

          {existing_user && (
            <p className="text-xs text-center text-base-content/60 mt-4">
              You'll be added to {invitation.account_name} alongside your existing account.
            </p>
          )}
        </div>
      </div>
    </AuthLayout>
  )
}
